/**
 * Arranque de la app embebida.
 *
 * El handshake de Nexo es obligatorio y tiene un orden fijo:
 *
 *   create({ clientId })  →  connect(nexo)  →  [suscribirse a la sincronía de rutas]  →  iAmReady(nexo)
 *
 * Hasta que no llega `iAmReady`, el admin muestra un loader y la app no se ve. Y después
 * de `iAmReady` la app **tiene que** estar escuchando `ACTION_NAVIGATE_SYNC`, o se rompe
 * el botón "atrás" del navegador. Por eso la suscripción va antes, en `App`.
 *
 * Del otro lado del handshake puede no haber nadie: si esta URL se abre suelta en el
 * navegador, nadie contesta. Ese caso termina en `FueraDelAdmin`, nunca en una pantalla
 * en blanco ni en un spinner eterno.
 */

// Los estilos de Nimbus van primero: definen las variables que usan los componentes.
import '@nimbus-ds/styles/dist/index.css';
// Y los nuestros después, que es lo único que los pisa. Una regla, explicada ahí adentro.
import './pantalla-angosta.css';

import { connect } from '@tiendanube/nexo';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { FueraDelAdmin } from './FueraDelAdmin';
import { Cargando } from './componentes/Estado';
import { nexo } from './nexo';

/**
 * Cuánto se espera al handshake antes de dar por hecho que no hay admin del otro lado.
 *
 * Nexo trae su propio timeout de 3 s, pero es suyo: si cambia de versión, o si la promesa
 * se queda colgada por cualquier motivo, el comerciante se come un spinner infinito. Este
 * es nuestro piso, un poco más largo para que el error propio de Nexo gane cuando llega.
 */
const TIEMPO_LIMITE_HANDSHAKE_MS = 5_000;

class HandshakeVencido extends Error {
  override readonly name = 'HandshakeVencido';
}

/** `connect`, pero con la garantía de que la promesa termina. */
async function conectarConTiempoLimite(ms: number): Promise<void> {
  let temporizador: ReturnType<typeof setTimeout> | undefined;
  const vencimiento = new Promise<never>((_, rechazar) => {
    temporizador = setTimeout(
      () => rechazar(new HandshakeVencido(`el admin no contestó en ${ms} ms`)),
      ms,
    );
  });

  try {
    await Promise.race([connect(nexo), vencimiento]);
  } finally {
    // Sin esto el timer queda vivo aunque el handshake haya salido bien.
    if (temporizador !== undefined) clearTimeout(temporizador);
  }
}

const contenedor = document.getElementById('root');
if (contenedor === null) throw new Error('falta el nodo #root en index.html');

const root = createRoot(contenedor);

// Algo en pantalla desde el primer frame: el handshake tarda, y en blanco no se entiende
// si está cargando o si se rompió.
root.render(
  <StrictMode>
    <Cargando texto="Conectando con tu tienda…" />
  </StrictMode>,
);

conectarConTiempoLimite(TIEMPO_LIMITE_HANDSHAKE_MS)
  .then(() => {
    root.render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  })
  .catch((error: unknown) => {
    // Abierta suelta fuera del iframe, o el admin no respondió. Para el comerciante es lo
    // mismo y la salida es la misma.
    console.error('nexo.connect', error);
    root.render(
      <StrictMode>
        <FueraDelAdmin />
      </StrictMode>,
    );
  });
